import {Link, useLoaderData, useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {IconRightArrowLightWeight} from '~/icons/home-page/brg-icon-app-logo-thick'
import {getTranslation} from '~/localization/translation'
import type {CategoriesDataProps, RootLoaderResponse} from '~/types'
import {getDomainConfig} from '~/domains'
import HomeCategoriesHeader from '../../../../components/home-categories/home-categories-header.js'
import PromotedProducts from './components/promoted-products.js'

const LegacyHomepage = () => {
  const {locale, domain, language} = useRouteLoaderData('root') as RootLoaderResponse
  const {categories, promotedProducts} = useLoaderData() as CategoriesDataProps
  const translations = getTranslation(language)
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)
  const isNonUS = locale.toLowerCase() !== 'us'
  const sendWidgetClickEvent = useReportWidgetClick()

  const [homePageMainRef] = useReportWidgetSeen({
    widget_name: 'home-page-l-categories',
    widget_place: 'top',
  })

  const [gridRef] = useReportWidgetSeen({
    widget_name: 'home-page-grid-level-1',
    widget_place: 'main',
  })

  return (
    <div className="w-full" ref={homePageMainRef}>
      <HomeCategoriesHeader />
      <main
        className={clsx(
          stylesBydomain.UTILS_MAIN_CONTAINER,
          'mx-auto flex w-full flex-col justify-center bg-white pt-14 text-2xl font-bold text-[#494747]',
          'utils_main_container',
        )}
      >
        {translations.Home?.bestCategories}
        <div
          id="line"
          className={clsx('mb-10 mt-6 h-1', stylesBydomain.HOMEPAGE_CONTENT_DIVIDER, 'homepage_content_divider')}
        />
        <div
          ref={gridRef}
          className="mb-14 grid grid-cols-1 gap-10 desktop:grid-cols-3 tablet:grid-cols-2 tablet:gap-10 mobile:grid-cols-1"
        >
          {categories &&
            !!categories?.length &&
            categories.slice(0, 9).map(({slug, name, l2Categories, imageData}, index) => {
              const showCategories = l2Categories?.length > 0 || isNonUS

              return (
                showCategories && (
                  <div
                    className="mb-4 flex flex-col"
                    key={`${name}-${index}`}
                    data-testid={`top-categories-list-item-${index + 1}`}
                  >
                    <Link
                      reloadDocument
                      to={isNonUS ? `/${slug}` : `/categories/${slug}`}
                      className="flex min-h-80 w-full flex-col items-start justify-between rounded-lg bg-[#F5F5F5] p-5"
                      aria-label={`explore ${name}`}
                      onClick={() => {
                        sendWidgetClickEvent({
                          widget_name: `framed-image-category-${slug}`,
                          widget_place: 'grid-item',
                          trigger_type: `framed-image`,
                        })
                      }}
                    >
                      <img
                        src={imageData?.url}
                        alt={slug}
                        className="flex h-44 w-full shrink-0 grow-0 object-contain mix-blend-multiply"
                      />
                      <p className="mb-2 mt-auto line-clamp-3 overflow-hidden pt-4 text-base font-bold leading-7 text-black">
                        {name}
                      </p>
                      {!isNonUS && (
                        <div className="flex flex-row items-center gap-2">
                          <p className="text-sm font-normal underline">{translations.Home.viewAll}</p>
                          <IconRightArrowLightWeight className="h-5 w-5" />
                        </div>
                      )}
                    </Link>
                    <div className="flex flex-col [&>*]:border-b [&>*]:border-b-neutral-200">
                      {l2Categories.slice(0, 3).map((category, innerIndex) => {
                        return (
                          <div key={innerIndex} className="flex flex-row items-center justify-between p-4">
                            <Link
                              reloadDocument
                              to={`/categories/${slug}/${category.slug}`}
                              className="text-sm font-normal"
                              aria-label={`go to ${category.name}`}
                              onClick={() => {
                                sendWidgetClickEvent({
                                  widget_name: `home-page-level-1-grid-item-${slug}`,
                                  widget_place: 'main',
                                  trigger_type: `subcategory-level2-${category.slug}`,
                                })
                              }}
                            >
                              <p className="line-clamp-1">{category.name}</p>
                            </Link>
                            <IconRightArrowLightWeight className="h-5 w-5" />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              )
            })}
        </div>
        {promotedProducts && promotedProducts?.length > 0 ? <PromotedProducts /> : null}
      </main>
    </div>
  )
}

export default LegacyHomepage
